import React, { useCallback, useMemo, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import { useSelector } from "react-redux";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import {
  HEADER_ACTION_BUTTON_WIDTH,
  designType,
  paginationFooter,
} from "services/constants/designTokens";
import { CorrectionStatusTiles } from "../AttendanceCorrection/CorrectionStatusTiles";
import { CorrectionApplyPanel } from "../CorrectionShared/CorrectionApplyPanel";
import {
  leavePalette,
  cardSx,
  canonicalStatus,
  SHIFT_CORRECTION_HEADERS,
  toShiftChangeRecord,
  toShiftFilterRow,
} from "./shiftCorrectionHelpers";
import { ShiftCorrectionTable } from "./ShiftCorrectionTable";
import { ViewShiftChange } from "./ViewShiftChange";

// 10 rows/page by default (matches the reference design), finer-grained than
// the app-wide [25,50,75,100].
const rowsPerPageOptions = [10, 25, 50, 100];

export const ShiftCorrection = ({ subModuleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // No access check existed here at all -- a viewer-only user could open
  // and submit the create panel with no gate. Same editor check + "block
  // with a toast" pattern the sibling My Request screens (Leave, Off Day
  // Duty, WorkAdjustment) already use, against this module's real
  // submodule name (verified against a live "My Workspace" payload).
  const checkAccess =
    checkEditorPermission(
      userScreenAccess?.module_details,
      "My Request",
      "Schedule Change",
    ) ||
    checkEditorPermission(
      userScreenAccess?.module_details,
      "My Request",
      "Shift Change",
    );

  // Live My Zone shift-change requests for the signed-in user.
  const {
    result: apiRows,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}my_zone/apply/shift_change/myzone-shift-change/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
  );

  const records = useMemo(
    () => (Array.isArray(apiRows) ? apiRows.map(toShiftChangeRecord) : []),
    [apiRows],
  );

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  // Default range is This Month (see getThisMonthRange).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "monthEnd" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);

  const handleDateRangeChange = (rangeStart, rangeEnd, rangeLabel) => {
    setStartDate(moment(new Date(rangeStart)).format("YYYY-MM-DD"));
    setEndDate(moment(new Date(rangeEnd)).format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel);
    setPage(0);
  };

  const dateWindowRecords = useMemo(
    () =>
      records.filter((r) => {
        const primaryInRange = r.date && r.date >= startDate && r.date <= endDate;
        const swapInRange =
          r.swap_date && r.swap_date >= startDate && r.swap_date <= endDate;
        return !r.date || primaryInRange || swapInRange;
      }),
    [records, startDate, endDate],
  );

  const counts = useMemo(
    () =>
      dateWindowRecords.reduce((acc, r) => {
        const s = canonicalStatus(r.status);
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {}),
    [dateWindowRecords],
  );

  const filterInputRows = useMemo(
    () =>
      dateWindowRecords
        .filter((r) => !statusFilter || canonicalStatus(r.status) === statusFilter)
        .map(toShiftFilterRow),
    [dateWindowRecords, statusFilter],
  );

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_shift_correction_saved_views",
    fullHeaders: SHIFT_CORRECTION_HEADERS,
    screenAccess: true,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name": userDetails?.username || userDetails?.user_name || "N/A",
      Type: "Schedule Change",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  // The apply panel persists the request itself (POST to the shift-change
  // endpoint); once it succeeds we just refetch the list.
  const handleCreate = () => {
    reCallApi(true);
    setStatusFilter("");
    setPage(0);
  };

  const openApply = () => {
    if (checkAccess) {
      setIsCreateOpen(true);
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to apply for a schedule change",
      );
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography sx={{ ...designType.pageTitle, color: leavePalette.text }}>
            Schedule Change
          </Typography>
          <Typography sx={{ ...designType.pageSubtitle, color: leavePalette.subText }}>
            View and manage your schedule change and week-off swap requests.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <ApprovalDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            dateRangeLabel={dateRangeLabel}
            onChange={handleDateRangeChange}
            includeCurrentDate
            showToday
          />
          {checkAccess ? (
            <PrimaryButton
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                minWidth: { xs: 0, sm: HEADER_ACTION_BUTTON_WIDTH },
                width: { xs: "100%", sm: "auto" },
              }}
              onClick={openApply}
            >
              New Request
            </PrimaryButton>
          ) : null}
        </Stack>
      </Stack>

      <CorrectionStatusTiles
        counts={counts}
        total={dateWindowRecords.length}
        activeStatus={statusFilter}
        onSelect={(s) => {
          setStatusFilter(s);
          setPage(0);
        }}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Box sx={{ ...cardSx, p: 2.5 }}>
            <CustomFilter
              title="Schedule Change"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={filterInputRows}
              filteredData={tableRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={true}
              showSaveButton={true}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search by date, shift or reason..."
            />

            <ShiftCorrectionTable
              headers={visibleHeaders}
              rows={paginatedRows}
              onRowClick={(row) => {
                setViewData(row);
                setIsViewOpen(true);
              }}
            />

            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${leavePalette.border}` }}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: tableRows.length,
                itemLabel: "requests",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {isCreateOpen ? (
        <CorrectionApplyPanel
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreate={handleCreate}
          title="Schedule Change Request"
          subtitle="Create a request to change your shift or swap your week-off."
          defaultCorrectionType="wrong_shift"
          mode="shift-change"
        />
      ) : null}

      {isViewOpen ? (
        <ViewShiftChange
          open={isViewOpen}
          selectedData={viewData}
          onClose={() => setIsViewOpen(false)}
          onUpdated={handleCreate}
          checkAccess={checkAccess}
        />
      ) : null}
    </Box>
  );
};

export default ShiftCorrection;
